import React, { useMemo, useEffect, useState, useRef, ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import shortid from 'shortid'
import classNames from 'classnames'
import { distance } from 'fastest-levenshtein'
import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'
import debounce from 'debounce'

import styles from '../component-styles/select-box-typeahead.module.scss'
import stylesTooltip from '../component-styles/tooltip.module.scss'
import Arrow from '../svgs/arrow'
import Button from './button'
import { getItemByKeyValue, sortData, hashCode } from '../helpers'
import { setCurrentDropDown, setCurrentTooltip } from '../store/actions'
import { UiState } from '../store/ui-reducer'
import { BetaLabel } from './counter'
import useMobile from '../hooks/useMobile'

const getUseValue = (items, keyName, keyValue, value) => {
  const res = getItemByKeyValue(items, keyValue, value)
  if (res !== -1) {
    return res[keyName]
  }
  return ''
}

interface Props {
  showClear?: boolean
  children?: React.ReactNode
  className?: string
  style?: any
  onInputChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  items: string[] | any[]
  id?: string
  errorMessage?: string
  showError?: boolean
  keyName?: string
  keyValue?: string
  value?: string | null
  showDefault?: boolean
  labels?: { items: string[]; title: string; icon?: string }[]
  icon?: string
  iconKey?: string
  sort?: null | 'asc' | 'desc'
  showTooltips?: boolean
  inline?: boolean
  elementActive?: boolean
  showValueWhenNotActive?: boolean
  selectOnBlur?: boolean
  lowerCaseDropDownItems?: boolean
  ignoreOrder?: boolean
  defaultError?: boolean
  showDropDownOnTop?: boolean
}

export default function SelectBoxTypeahead({
  showClear,
  style,
  className,
  disabled,
  placeholder,
  onInputChange,
  items,
  id,
  children,
  errorMessage,
  showError,
  value = null,
  labels = [],
  showDefault = false,
  keyName = 'optionName',
  keyValue = 'optionValue',
  icon = '',
  iconKey = '',
  sort = null,
  showTooltips = false,
  inline = false,
  elementActive = false,
  showValueWhenNotActive = false,
  selectOnBlur = false,
  lowerCaseDropDownItems = false,
  ignoreOrder = false,
  defaultError = false,
  showDropDownOnTop = false,
}: Props): React.ReactElement {
  const [position, setPosition] = useState<any>(null)
  const [readOnly, setReadOnly] = useState(true)
  const [active, setActive] = useState(false)
  const useValue =
    items.length > 0 && typeof items[0] === 'string'
      ? value
      : getUseValue(items, keyName, keyValue, value)
  const [searchTerm, setSearchTerm] = useState(useValue || '')
  const [termSelected, setTermSelected] = useState(false)
  const { currentDropDown, currentTooltip } = useSelector(
    ({ ui }: { ui: UiState }) => ui,
  )
  const isMobile = useMobile(769)

  const dispatch = useDispatch()
  const ref: any = useRef(null)
  const inputRef: any = useRef(null)
  let inputInFocus = false

  const handleAction = (): void => {
    if (!inputInFocus && !disabled) {
      const activeState = !active
      setActive(activeState)
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value: val } = event.target
    setSearchTerm(val)
  }

  const itemsFiltered = useMemo(() => {
    const labelsFullMatch = labels.filter((item: any): boolean => {
      const { title: toSearch } = item
      if (toSearch && searchTerm !== '') {
        const pattern = searchTerm
          .replace(/[^a-zA-Z0-9 ]/g, '')
          .split('')
          .join('.*')
        return toSearch.toLowerCase().match(new RegExp(pattern, 'i')) !== null
      }
      return false
    })

    const itemIdsMerged = labelsFullMatch
      .map((item) => {
        return item.items
      })
      .join()

    const result = items.filter((item: string | any): boolean => {
      const toSearch = typeof item === 'object' ? item[keyName] : item
      const toSearchId = typeof item === 'object' ? item[keyValue] : item
      if (toSearch && searchTerm !== '' && searchTerm !== null) {
        const pattern = searchTerm
          .replace(/[^a-zA-Z0-9 ]/g, '')
          .split('')
          .join('.*')
        return (
          itemIdsMerged.indexOf(toSearchId) !== -1 ||
          toSearch.toLowerCase().match(new RegExp(pattern, 'i')) !== null
        )
      }
      return termSelected || searchTerm === ''
    })
    if (searchTerm !== '' && searchTerm !== null) {
      result.sort((a, b) => {
        const distA = distance(
          searchTerm,
          typeof a === 'object' ? a[keyName] : a,
        )
        const distB = distance(
          searchTerm,
          typeof b === 'object' ? b[keyName] : b,
        )
        return distA - distB
      })
    } else if (!ignoreOrder) {
      return sortData(result, keyName, true, null, true)
    }
    return result
    // eslint-disable-next-line
  }, [searchTerm, termSelected])

  const getFirstItem = () => {
    if (
      labels.length === 0 ||
      itemsFiltered.length === 0 ||
      (itemsFiltered.length > 0 && typeof itemsFiltered[0] !== 'object')
    ) {
      return null
    }
    let sortedLabelItems: any[] = []
    labels.forEach((label) => {
      const labelItems = itemsFiltered.filter((item: string | any): boolean => {
        return label.items.indexOf(item[keyValue]) !== -1
      })
      let sorted = labelItems
      if (sort) {
        const sortOrder = sort === 'asc'
        sorted = sortData(labelItems, keyName, sortOrder, null, true)
      }
      sortedLabelItems = sortedLabelItems.concat(sorted)
    })
    return sortedLabelItems.length > 0 ? sortedLabelItems[0] : null
  }

  const onDoneEditing = () => {
    inputInFocus = false
    if (!termSelected) {
      // check for full match of search term
      const fullMatch = items.filter((item: any): boolean => {
        const toSearch = typeof item === 'object' ? item[keyName] : item
        if (toSearch && searchTerm !== '') {
          return (
            toSearch.toLowerCase() === searchTerm.toLowerCase() &&
            toSearch.length === searchTerm.length
          )
        }
        return false
      })
      if (fullMatch.length > 0) {
        const item = fullMatch[0]
        const val = typeof item === 'object' ? item[keyValue] : item
        const text = typeof item === 'object' ? item[keyName] : item
        // eslint-disable-next-line
        // debugger
        onInputChange(val)
        setSearchTerm(text)
        inputRef.current.blur()
        setActive(false)
      } else if (itemsFiltered.length > 0) {
        // set value to first item in the list
        const i = getFirstItem()
        if (i) {
          onInputChange(i[keyValue])
          setSearchTerm(i[keyName])
        } else {
          onInputChange(
            typeof itemsFiltered[0] === 'object'
              ? itemsFiltered[0][keyValue]
              : itemsFiltered[0],
          )
          setSearchTerm(
            typeof itemsFiltered[0] === 'object'
              ? itemsFiltered[0][keyName]
              : itemsFiltered[0],
          )
        }
        inputRef.current.blur()
        setActive(false)
      } else if (fullMatch.length === 0) {
        if (showDefault && value) {
          onInputChange(value)
        } else {
          onInputChange('')
        }
        setSearchTerm(useValue)
      }
    }
  }

  useEffect(() => {
    if (currentDropDown !== '') {
      if (active && currentDropDown !== id) {
        setActive(false)
      }
    }
  }, [currentDropDown])

  useEffect(() => {
    dispatch(setCurrentDropDown({ id: active ? id : '' }))
    if (!active) {
      if (inputRef && inputRef.current) {
        const { value: inputValue } = inputRef.current
        if (inputValue === '' && showDefault) {
          setSearchTerm(useValue)
        } else if (selectOnBlur && inputValue !== '') {
          onDoneEditing()
        }
      }
    }
    // eslint-disable-next-line
  }, [active])

  const setDefault = (toggle = false) => {
    if (!showDefault) {
      onInputChange('')
    }
    setSearchTerm('')
    setTermSelected(false)
    inputInFocus = true
    if (toggle) {
      setActive(!active)
    } else {
      setActive(true)
    }
  }

  const renderItem = (item): React.ReactElement => {
    const val = typeof item === 'object' ? item[keyValue] : item
    const text = typeof item === 'object' ? item[keyName] : item
    const isBeta = typeof item === 'object' ? item.isBeta || false : false
    const tooltip = typeof item === 'object' ? item.tooltip || '' : ''

    const iconPath =
      typeof item === 'object' && iconKey !== '' ? item[iconKey] : ''
    return (
      <div key={`${val}${text}`} className={styles.hoverItem}>
        <Button
          className={classNames(styles.button, {
            [styles.lowerCase]: lowerCaseDropDownItems,
          })}
          type="selectBox"
          onMouseOver={debounce((buttonRef) => {
            if (!isMobile && showTooltips && buttonRef) {
              const boundedSize = buttonRef.getBoundingClientRect()
              if (boundedSize.width > 0) {
                setPosition(boundedSize)
                dispatch(setCurrentTooltip({ id: active ? val : '' }))
              }
            }
          }, 200)}
          onMouseLeave={() => {
            if (showTooltips) {
              dispatch(setCurrentTooltip({ id: '' }))
            }
          }}
          onClick={(): void => {
            onInputChange(val)
            setSearchTerm(text)
            setTermSelected(true)
            setActive(false)
          }}
        >
          {iconPath !== '' && (
            <img src={iconPath} alt="" className={styles.icon} />
          )}
          <span>{text}</span>
          {isBeta && <BetaLabel />}
          {showTooltips && tooltip !== '' && (
            <span className={styles.dotWrapper}>
              <span className={classNames(stylesTooltip.dot, styles.dot)} />
            </span>
          )}
        </Button>
        {showTooltips && tooltip !== '' && isMobile && (
          <div className={styles.tooltip}>
            <ReactMarkdown plugins={[gfm]} linkTarget="_blank">
              {tooltip}
            </ReactMarkdown>
          </div>
        )}
      </div>
    )
  }

  const renderItems = () => {
    if (labels.length > 0) {
      const result = labels.map((label) => {
        const labelItems = itemsFiltered.filter(
          (item: string | any): boolean => {
            return label.items.indexOf(item[keyValue]) !== -1
          },
        )
        let sorted = labelItems
        if (sort && !ignoreOrder) {
          const sortOrder = sort === 'asc'
          sorted = sortData(labelItems, keyName, sortOrder, null, true)
        }

        if (sorted.length > 0) {
          return (
            <span key={shortid.generate()}>
              <span className={styles.itemLabel}>
                <span>
                  {label.icon && (
                    <img src={label.icon} className={styles.labelIcon} alt="" />
                  )}
                  <>{label.title}</>
                </span>
              </span>
              {sorted.map((item) => renderItem(item))}
            </span>
          )
        }
        return null
      })
      return result
    }

    return itemsFiltered.map((item) => renderItem(item))
  }

  const wrapperClassNames = classNames(className, styles.typeahead, {
    [styles.showDropDownOnTop]: showDropDownOnTop,
    [styles.containerActive]: active,
    [styles.container]: !active,
    [styles.disabled]: disabled,
    [styles.inline]: inline,
    [styles.error]:
      defaultError ||
      (showError && itemsFiltered.length === 0 && items.length !== 0),
    [styles.hasIcon]: icon !== '',
  })
  // const isEmptyValue = (useValue === '' || useValue === null) && searchTerm === ''
  const isEmptyValue = searchTerm === ''

  const renderTooltip = useMemo(() => {
    if (showTooltips && currentTooltip !== '') {
      const found = getItemByKeyValue(items, keyValue, currentTooltip)
      if (found !== -1) {
        const tooltip = typeof found === 'object' ? found.tooltip || '' : ''
        if (tooltip !== '' && position) {
          const { top, width } = position
          return (
            <div
              className={styles.tooltip}
              style={{
                // left: `${left}px`,
                marginLeft: `${width}px`,
                top: `${top + window.scrollY}px`,
              }}
            >
              <ReactMarkdown plugins={[gfm]} linkTarget="_blank">
                {tooltip}
              </ReactMarkdown>
            </div>
          )
        }
      }
    }
    return null
  }, [currentTooltip, position])

  if (showValueWhenNotActive && !elementActive && !active) {
    return <span>{useValue}</span>
  }

  return (
    <>
      <div
        ref={ref}
        className={wrapperClassNames}
        onClick={handleAction}
        onKeyDown={(): void => {
          if (!inputInFocus && !disabled) {
            setActive(true)
          }
        }}
        onMouseLeave={() => {
          if (showTooltips) {
            dispatch(setCurrentTooltip({ id: '' }))
          }
        }}
        onBlur={(e: any): void => {
          if (showTooltips) {
            dispatch(setCurrentTooltip({ id: '' }))
          }
          if (
            !inputInFocus &&
            ref &&
            ref.current &&
            !ref.current.contains(e.relatedTarget)
          ) {
            setActive(false)
          }
        }}
        style={style}
        role="button"
        tabIndex={-1}
      >
        {icon !== '' && <img src={icon} alt="" className={styles.icon} />}
        <input
          ref={inputRef}
          id={`id-${hashCode(id)}`}
          disabled={disabled}
          className={styles.input}
          placeholder={placeholder}
          value={searchTerm || ''}
          onKeyUp={(event): void => {
            setTermSelected(false)
            if (event.keyCode === 13) {
              onDoneEditing()
            }
          }}
          autoComplete="off"
          onClick={(): void => {
            setDefault()
          }}
          size={`${searchTerm || placeholder || ''}`.length + 1}
          readOnly={readOnly}
          type="search"
          onChange={handleChange}
          onFocus={() => {
            setReadOnly(false)
          }}
        />
        {showClear && !isEmptyValue && (
          <Button
            type="clear"
            className={styles.clearButton}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation()
              onInputChange('')
              setSearchTerm('')
              setTermSelected(true)
              setActive(false)
            }}
            ariaLabel="cancel"
          />
        )}

        <Button
          disabled={disabled}
          className={styles.expandButton}
          type="none"
          onClick={(): void => {
            setDefault(true)
          }}
        >
          <Arrow fill="#D4366A" />
        </Button>
        {active && (
          <>
            <div
              className={classNames(styles.expanded, {
                [styles.withTooltips]: showTooltips,
              })}
            >
              {itemsFiltered.length > 0 ? (
                renderItems()
              ) : (
                <p className={styles.empty}>
                  {errorMessage || 'No items match your search'}
                </p>
              )}
              {children}
            </div>
          </>
        )}
        {active && <>{renderTooltip}</>}
      </div>
    </>
  )
}
